// src/main.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation, useRoutes } from 'react-router-dom';
import routes from './router/routes';
import './index.css';
import './assets/styles/theme.scss';
import './assets/styles/semantic.scss';
import './assets/styles/antd-overrides.scss';
import 'virtual:svg-icons-register';
import './utils/i18n';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';
import type { Locale } from 'antd/es/locale';
import useThemeStore from './store/useThemeStore';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import MessageBridge from './context/MessageBridge';
import { initLanguageSync, cleanupLanguageSync } from './utils/languageSync';
import { getAntdThemeConfig } from './theme/antdTheme';
import GlobalUpgradeModal from './components/GlobalUpgradeModal';
import BackgroundPollingManager from './components/BackgroundPollingManager';
import { computeDaysSince, initAnalytics, recordPageView, startSession, endSession, trackInitialPageLoadSlow } from './utils/analytics';
import { useUserStore } from './store/useUserStore';

// 语言映射配置
const LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  fr: frFR,
};

const DAYJS_LOCALE_MAP: Record<string, string> = {
  en: 'en',
  fr: 'fr',
};

// 全局空状态渲染组件
interface GlobalEmptyProps {
  text: string;
  size?: 'small' | 'default';
}

const GlobalEmpty: React.FC<GlobalEmptyProps> = ({ text, size = 'default' }) => (
  <div className={`ant-empty-custom ${size === 'small' ? 'ant-empty-custom-small' : ''}`}>
    <svg className="ant-empty-custom-icon" aria-hidden="true">
      <use xlinkHref="#icon-empty" />
    </svg>
    <p className="ant-empty-custom-text">{text}</p>
  </div>
);

function AppRouter() {
  // 根据配置直接渲染整棵路由树
  return useRoutes(routes);
}

function AnalyticsTracker() {
  const location = useLocation();
  const initialLoadTrackedRef = useRef(false);
  const { user } = useUserStore();

  useEffect(() => {
    initAnalytics();
    recordPageView(location.pathname);
    if (!initialLoadTrackedRef.current) {
      trackInitialPageLoadSlow(location.pathname);
      initialLoadTrackedRef.current = true;
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleUnload = () => {
      endSession();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      endSession();
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    startSession({
      user_plan: user.subscription_plan || 'freemium',
      days_since_signup: computeDaysSince(user.created_at),
    });
  }, [user?.id, user?.subscription_plan, user?.created_at]);

  return null;
}

function App() {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const [locale, setLocale] = useState(() => {
    const storedLanguage = localStorage.getItem('i18nextLng') || 'en';
    return LOCALE_MAP[storedLanguage] || enUS;
  });

  useEffect(() => {
    initLanguageSync();
    return () => cleanupLanguageSync();
  }, []);

  useEffect(() => {
    const newLocale = LOCALE_MAP[i18n.language] || enUS;
    setLocale(newLocale);
    dayjs.locale(DAYJS_LOCALE_MAP[i18n.language] || 'en');
  }, [i18n.language]); // 当语言变化时触发

  // 根据主题设置 document 属性
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // 全局空状态渲染函数（根据组件类型返回不同尺寸）
  const renderEmpty = (componentName?: string) => {
    // Select、Cascader、TreeSelect 等下拉组件使用小尺寸
    const smallComponents = ['Select', 'Cascader', 'TreeSelect', 'Transfer', 'Mentions'];
    const size = componentName && smallComponents.includes(componentName) ? 'small' : 'default';
    return <GlobalEmpty text={t('common.noData')} size={size} />;
  };

  return (
    <ConfigProvider locale={locale} theme={getAntdThemeConfig(isDarkMode)} renderEmpty={renderEmpty}>
      <AntdApp>
        <MessageBridge>
          <BrowserRouter>
            <AnalyticsTracker />
            <AppRouter />
            <GlobalUpgradeModal />
            <BackgroundPollingManager />
          </BrowserRouter>
        </MessageBridge>
      </AntdApp>
    </ConfigProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
